<template>
  <!-- ----------------- -->
  <!-- TOP ROW (HEADER)  -->
  <!-- ----------------- -->
  <div>
    <!-- ------------------------------------------------------------>
    <!-- MAIN CONTAINER -->
    <!-- -------------- -->
    <div
      v-if="isReady"
      class="row gx-1"
    >
      <div class="col-12 source-row bb-tooltip-wrapper">
        <div class="source-grid">
          <div
            class="source-column"
            v-for="source in powerSources"
            :key="getSourceKey(source)"
          >
            <div class="row gx-1 mt-2 align-items-center">
              <!-- ----------------------------------------------------- -->
              <!-- source -->
              <!-- ----------------------------------------------------- -->
              <div class="col-5 text-center">
                <div class="form-group">
                  <ToolTip
                    :text="displaySourceText(source)"
                    position="top"
                  >
                    <div class="form-floating">
                      <input
                        readonly
                        class="form-control text-left"
                        :value="getSourceName(source)"
                        :id="`src-${getSourceKey(source)}`"
                        disabled
                      />
                      <label
                        class="readonly bb-float-label optional"
                        :for="`src-${getSourceKey(source)}`"
                        >Source<span class="bb-orange-circle"></span
                      ></label>
                    </div>
                  </ToolTip>
                </div>
              </div>
              <!-- ----------------------------------------------------- -->
              <!-- tot -->
              <!-- ----------------------------------------------------- -->
              <div class="col-2 text-center">
                <div class="form-group">
                  <div class="form-floating">
                    <input
                      readonly
                      class="form-control text-center"
                      :value="source.pwr_storage"
                      :id="`src-tot-${getSourceKey(source)}`"
                      disabled
                    />
                    <label
                      class="readonly bb-float-label optional"
                      :for="`src-tot-${getSourceKey(source)}`"
                    >
                      Total
                    </label>
                  </div>
                </div>
              </div>
              <!-- ----------------------------------------------------- -->
              <!-- cur -->
              <!-- ----------------------------------------------------- -->
              <div
                :class="
                  lockCharacterMp && source.type === 'character'
                    ? 'ps-3 col-2 text-center'
                    : 'ps-3 col-5'
                "
              >
                <template v-if="lockCharacterMp && source.type === 'character'">
                  <div class="form-group">
                    <div class="form-floating">
                      <input
                        readonly
                        class="form-control text-center"
                        :value="Number(source.pwr_storage_cur) || 0"
                        :id="`src-cur-${getSourceKey(source)}`"
                        disabled
                      />
                      <label
                        class="readonly bb-float-label optional"
                        :for="`src-cur-${getSourceKey(source)}`"
                        >Cur</label
                      >
                    </div>
                  </div>
                </template>
                <template v-else>
                  <CounterComponent
                    :pass_ref="`src-cur-${getSourceKey(source)}`"
                    :pass_label="'Cur'"
                    :pass_count="Number(source.pwr_storage_cur) || 0"
                    :pass_min="0"
                    :pass_max="Number(source.pwr_storage) || 0"
                    :pass_digits="2"
                    :disabled="lockCharacterMp && source.type === 'character'"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'
import CounterComponent from './Counter.vue'

import ToolTip from '../components/ToolTip.vue'

export default {
  name: 'SheetMagicPoints',
  emits: ['update-power-source'],
  //========================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    power_storage_items: {
      type: Array,
      required: true,
    },
    lockCharacterMp: {
      type: Boolean,
      required: false,
      default: false,
    },
    maxVisibleItemSources: {
      type: Number,
      required: false,
      default: 3,
    },
  },
  //========================================================
  // components
  //------------
  components: {
    ToolTip,
    CounterComponent,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // data
  //------
  data() {
    return {
      local_character: null,
      counterListeners: {},
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    character: {
      handler(newChar) {
        if (!newChar) return
        this.local_character = this.character
      },
      deep: true,
      immediate: true,
    },
    powerSources: {
      handler() {
        this.syncCounterListeners()
      },
      deep: true,
      immediate: true,
    },
  },
  //========================================================
  // computed
  //-----------
  computed: {
    isReady() {
      if (this.local_character != null) {
        return true
      }
      return false
    },
    visiblePowerStorageItems() {
      const maxVisibleItems = Number(this.maxVisibleItemSources) || 0

      if (maxVisibleItems <= 0) {
        return []
      }

      const sortedPowerStorageItems = [
        ...(this.power_storage_items || []),
      ].sort((left, right) => {
        const storageDiff =
          (Number(right?.pwr_storage) || 0) - (Number(left?.pwr_storage) || 0)

        if (storageDiff !== 0) {
          return storageDiff
        }

        const leftName = this.getSourceName(left)
        const rightName = this.getSourceName(right)
        return leftName.localeCompare(rightName)
      })

      return sortedPowerStorageItems.slice(0, maxVisibleItems)
    },
    powerSources() {
      // for mp: type = character, id = null, name = 'Magic Pts', pwr_storage = mp, pwr_storage_cur = mp_cur
      // for item: type = item, id = item_id, name = item_name, pwr_storage = item_pwr_storage, pwr_storage_cur = item_pwr_storage_cur

      return [
        {
          type: 'character',
          id: null,
          name: 'Magic Points',
          pwr_storage: this.local_character?.mp ?? 0,
          pwr_storage_cur: this.local_character?.mp_cur ?? 0,
        },
        ...this.visiblePowerStorageItems,
      ]
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    displaySourceText(source) {
      return `${source.name}`
    },
    getSourceKey(source) {
      return source?.id || source?.item || source?.name || 'source'
    },
    getSourceCounterRef(source) {
      return `src-cur-${this.getSourceKey(source)}`
    },
    getSourceName(source) {
      const displayName = source?.display_name?.trim()
      if (displayName) {
        return displayName
      }
      return source?.name || ''
    },
    handleCounterValue(source, rawValue) {
      if (
        !source ||
        rawValue === null ||
        rawValue === undefined ||
        rawValue === ''
      ) {
        return
      }

      const nextValue = Number(rawValue)
      if (Number.isNaN(nextValue)) {
        return
      }

      const maxValue = Number(source?.pwr_storage) || 0
      const clampedValue = Math.max(0, Math.min(nextValue, maxValue))

      if (source.type === 'character') {
        this.$emit('update-power-source', {
          type: 'profile',
          key: 'mp_cur',
          value: clampedValue,
        })
        return
      }

      this.$emit('update-power-source', {
        type: 'items',
        id: source.id,
        name: source.name,
        key: 'pwr_storage_cur',
        value: clampedValue,
      })
    },
    syncCounterListeners() {
      if (!this.emitter?.on || !this.emitter?.off) {
        return
      }

      const nextRefs = new Set(
        this.powerSources.map((source) => this.getSourceCounterRef(source)),
      )

      Object.entries(this.counterListeners).forEach(([ref, listener]) => {
        if (nextRefs.has(ref)) {
          return
        }

        this.emitter.off(ref, listener)
        delete this.counterListeners[ref]
      })

      this.powerSources.forEach((source) => {
        const ref = this.getSourceCounterRef(source)
        if (this.counterListeners[ref]) {
          return
        }

        const listener = (value) => {
          const activeSource = this.powerSources.find(
            (candidate) => this.getSourceCounterRef(candidate) === ref,
          )
          this.handleCounterValue(activeSource, value)
        }

        this.counterListeners[ref] = listener
        this.emitter.on(ref, listener)
      })
    },
    clearCounterListeners() {
      if (!this.emitter?.off) {
        this.counterListeners = {}
        return
      }

      Object.entries(this.counterListeners).forEach(([ref, listener]) => {
        this.emitter.off(ref, listener)
      })
      this.counterListeners = {}
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    // console.log('magic points = ', this.local_character)
  },
  beforeUnmount() {
    this.clearCounterListeners()
  },
}
</script>

<style scoped>
.source-row {
  padding-left: 0;
  padding-right: 0;
}

.source-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 1.25rem;
  row-gap: 0.75rem;
}

.source-column {
  position: relative;
  min-width: 0;
  width: 100%;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

.source-column::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 3px;
  background-color: rgba(82, 81, 81, 0.7);
  pointer-events: none;
}

.source-column:last-child::after {
  content: none;
}

@media (max-width: 1199.98px) {
  .source-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .source-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
